<template>
  <div
    v-if="items.length"
    :class="['shop-property-changes', `shop-property-changes--variant-${variant}`]"
  >
    <div data-part="row-header">
      <div></div>
      <div>{{ $t('before') }}</div>
      <div>{{ $t('change') }}</div>
      <div>{{ $t('after') }}</div>
    </div>
    <div
      v-for="item in items"
      :key="item.key"
      data-part="row"
      :data-change="item.delta < 0 ? 'subtract' : 'add'"
    >
      <div data-part="label">{{ $tc(item.key) }}</div>
      <div data-part="old">
        {{ format(item.old, item.property) }}
      </div>
      <div data-part="delta">
        {{ format(item.delta, item.property, true) }}
      </div>
      <div data-part="new">
        {{ format(item.new, item.property) }}
      </div>
    </div>
  </div>
</template>

<script>
import { toArray, getReferencePropertyChanges, isNumeric } from '../../utils'

const Units = {
  SquareMetre: {},
  Metre: {
    style: 'unit',
    unit: 'meter',
  },
  Currency: {
    style: 'currency',
    currencyDisplay: 'symbol',
    trailingZeroDisplay: 'stripIfInteger',
  },
  Int: {},
  Float: {},
}

export default {
  inject: ['shop'],
  props: {
    value: [Object, Array],
    variant: {
      type: String,
      default: 'short',
    },
  },
  computed: {
    all() {
      return getReferencePropertyChanges(this.shop)
    },
    items() {
      if (!this.value)
        return this.all
          .filter(({ key }) => key in this.properties)
          .map(({ key, delta }) => {
            const property = this.properties[key]
            const value = parseFloat(property.value)

            return {
              key,
              delta,
              old: value,
              new: value + delta,
              property,
            }
          })
      return toArray(this.value)
        .filter(({ key }) => key in this.properties)
        .map(({ key, delta }) => {
          const property = this.properties[key]
          const value = parseFloat(property.value)
          const all = this.all.find(a => a.key === key)

          return {
            key,
            delta,
            old: value + (all.delta - delta),
            new: value + all.delta,
            property,
          }
        })
    },
    properties() {
      return Object.fromEntries(
        (this.$path('shop.reference.properties') || [])
          .filter(p => isNumeric(p.value))
          .map(({ key, value, unit, suffix }) => [key, { value, unit, suffix }]),
      )
    },
  },
  methods: {
    format(value, property, delta) {
      const options = { ...Units[property.unit] }

      if (property.unit === 'Currency') options.currency = this.shop.metadata.currency

      if (delta) options.signDisplay = 'always'

      const formatted = new Intl.NumberFormat('nb-NO', options).format(value)

      if (property.unit === 'SquareMetre') return `${formatted} ${property.suffix}`
      return formatted
    },
  },
}
</script>

<style lang="scss">
.shop-property-changes {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  [data-change='add'] {
    [data-part='delta'] {
      color: hsl(var(--success-h), var(--success-s), 20%);
    }
  }

  [data-change='subtract'] {
    [data-part='delta'] {
      color: hsl(var(--error-h), var(--error-s), 40%);
    }
  }

  [data-part^='row'] {
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / -1;

    gap: 1.5rem;

    & > *:nth-child(n + 2) {
      text-align: end;
    }
  }

  &--variant-short {
    display: grid;
    grid-template-columns: 1fr auto auto;

    [data-part^='row'] {
      gap: 0.5rem;
    }

    [data-part='delta'] {
      &:before {
        content: '(';
        opacity: 0.3;
      }
      &:after {
        content: ')';
        opacity: 0.3;
      }
    }

    [data-part='row-header'],
    [data-part='old'] {
      display: none;
    }
  }

  &--variant-delta {
    display: grid;
    grid-template-columns: 1fr auto;

    [data-part^='row'] {
      gap: 1rem;
    }

    [data-part='label'] {
      &:after {
        content: ':';
      }
    }

    [data-part='row-header'],
    [data-part='old'],
    [data-part='new'] {
      display: none;
    }
  }

  &--variant-long {
    display: grid;
    grid-template-columns: 1fr auto auto auto;

    [data-part='row-header'] {
      font-size: 0.8em;
      color: rgba(black, 0.5);
    }
  }
}
</style>
../../utils
